import i18next from "i18next";
import {get, isString} from "lodash-es";
import {showGlobalError} from "modules/actions";
import {userLogoutSuccess} from "modules/actions/user";
import {isLoggedIn} from "modules/selectors/user";
import type {TSagaAction} from "modules/types";
import {ApiError} from "modules/utils";
import {appendReloadMarker, isGameReloaded} from "modules/utils/mlbConfig";
import type {SagaIterator} from "redux-saga";
import {call, put, select} from "typed-redux-saga";
import * as Sentry from "@sentry/browser";

type TPayload = ApiError | Error | string;

const defaultErrorHandler = (payload: TPayload) => {
	if (isString(payload)) {
		throw new Error(payload);
	}

	if (isString(payload.message)) {
		throw new Error(payload.message);
	}

	if (payload instanceof ApiError) {
		throw new Error(payload.message);
	}
};

const extractErrorMessage = (payload: TPayload) => {
	if (isString(payload)) {
		return payload;
	}

	if (isString(payload.message)) {
		return payload.message;
	}

	if (payload instanceof ApiError) {
		return payload.message;
	}

	return "";
};

const onError = {
	*[ApiError.AUTH_REQUIRED](error: TPayload): SagaIterator {
		if (yield* select(isLoggedIn)) {
			yield* put(userLogoutSuccess());
		}

		if (!isGameReloaded()) {
			appendReloadMarker();
			return;
		}

		const message = i18next.t("global.errors.session_expired", extractErrorMessage(error));
		Sentry.captureMessage(`Unhandled global error: ${extractErrorMessage(error)}`);
		yield* call(defaultErrorHandler, message);
	},
};

export const errorsManagerSaga = function* ({payload}: TSagaAction<TPayload>): SagaIterator {
	const error_code = get(payload, "code", 0);

	try {
		const onErrorHandler = get(onError, error_code, defaultErrorHandler);
		yield* call(onErrorHandler, payload);
	} catch (err) {
		yield* put(
			showGlobalError({
				message: get(err, "message", ""),
				code: error_code || 0,
			})
		);
	}
};
